import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Search,
  Filter,
  Plus,
  UserCheck,
  Users,
  ArrowRight,
  CheckCircle,
  Clock,
  AlertCircle,
  UserPlus,
  RefreshCw,
  X
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

export default function AdminAssignments() {
  const [students, setStudents] = useState([]);
  const [counselors, setCounselors] = useState([]);
  const [agents, setAgents] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Assignment modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedCounselor, setSelectedCounselor] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [studentsRes, counselorsRes, agentsRes, requestsRes] = await Promise.all([
        adminAPI.getStudents().catch(err => {
          console.error('Error fetching students:', err);
          return { students: [] };
        }),
        adminAPI.getCounselors().catch(err => {
          console.error('Error fetching counselors:', err);
          return { counselors: [] };
        }),
        adminAPI.getAgents().catch(err => {
          console.error('Error fetching agents:', err);
          return { agents: [] };
        }),
        adminAPI.getAllServiceRequests().catch(err => {
          console.error('Error fetching requests:', err);
          return { serviceRequests: [] };
        })
      ]);

      console.log('Counselors Response:', counselorsRes);
      console.log('Agents Response:', agentsRes);

      setStudents(studentsRes.students || []);
      setCounselors(counselorsRes.counselors || []);
      setAgents(agentsRes.agents || []);
      setServiceRequests(requestsRes.serviceRequests || []);
    } catch (error) {
      toast.error('Failed to fetch data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Get unassigned service requests (pending assignment)
  const unassignedRequests = serviceRequests.filter(
    r => r.status === 'PENDING_ADMIN_ASSIGNMENT' && !r.assignedCounselor
  );

  // Get assigned requests
  const assignedRequests = serviceRequests.filter(
    r => r.assignedCounselor || r.status === 'ASSIGNED'
  );

  // Open assignment modal
  const openAssignModal = (request) => {
    setSelectedRequest(request);
    setSelectedStudent(request.student);
    setSelectedCounselor(request.assignedCounselor?._id || '');
    setSelectedAgent(request.assignedAgent?._id || '');
    setAssignModalOpen(true);
  };

  // Handle assignment
  const handleAssign = async () => {
    // Either counselor OR agent must be selected
    if (!selectedCounselor && !selectedAgent) {
      toast.error('Please select at least a counselor or an agent');
      return;
    }

    setAssigning(true);
    try {
      await adminAPI.assignServiceRequest(selectedRequest.requestId || selectedRequest.serviceRequestId || selectedRequest._id, {
        counselorId: selectedCounselor || undefined,
        agentId: selectedAgent || undefined
      });

      toast.success('Assignment successful!');
      setAssignModalOpen(false);
      setSelectedRequest(null);
      setSelectedStudent(null);
      setSelectedCounselor('');
      setSelectedAgent('');
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Assignment error:', error);
      toast.error(error.response?.data?.message || 'Failed to assign');
    } finally {
      setAssigning(false);
    }
  };

  // Filter students by search term
  const filteredUnassigned = unassignedRequests.filter(request => {
    const student = request.student;
    if (!student) return false;
    const name = `${student.user?.firstName || student.firstName || ''} ${student.user?.lastName || student.lastName || ''}`.toLowerCase();
    return name.includes(searchTerm.toLowerCase());
  });

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-assignments-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-outfit font-bold text-gray-900">Assignments</h1>
            <p className="text-gray-600">Manage student-counselor assignments</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4 bg-white border-stone-200/80">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{unassignedRequests.length}</p>
                <p className="text-sm text-gray-600">Pending Assignment</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border-stone-200/80">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{assignedRequests.length}</p>
                <p className="text-sm text-gray-600">Assigned</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border-stone-200/80">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{counselors.length}</p>
                <p className="text-sm text-gray-600">Available Counselors</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border-stone-200/80">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{agents.length}</p>
                <p className="text-sm text-gray-600">Available Agents</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Search */}
        <Card className="p-4 bg-white border-stone-200/80">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search students..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-assignments-input"
              />
            </div>
            <Button variant="outline" data-testid="filter-assignments-btn">
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </Button>
          </div>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-[#F2575F] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Unassigned Requests */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-outfit font-bold text-gray-900 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  Pending Assignment
                </h2>
                <Badge className="bg-amber-100 text-amber-700">{filteredUnassigned.length}</Badge>
              </div>

              <div className="space-y-3 max-h-[400px] overflow-y-auto" data-testid="unassigned-students-list">
                {filteredUnassigned.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">All requests are assigned!</p>
                ) : (
                  <AnimatePresence>
                    {filteredUnassigned.map((request, index) => {
                      const student = request.student;
                      const firstName = student?.user?.firstName || student?.firstName || 'Unknown';
                      const lastName = student?.user?.lastName || student?.lastName || '';

                      return (
                        <motion.div
                          key={request.requestId || request._id || index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -10 }}
                          transition={{ delay: index * 0.05 }}
                          className="flex items-center justify-between p-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-white font-semibold">
                              {firstName[0]}{lastName[0]}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{firstName} {lastName}</p>
                              <p className="text-xs text-gray-500">
                                {request.serviceType || 'Service Request'}
                                {request.createdAt && ` • ${formatDistanceToNow(new Date(request.createdAt), { addSuffix: true })}`}
                              </p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => openAssignModal(request)}
                            data-testid={`assign-student-${index}`}
                          >
                            Assign
                            <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                )}
              </div>
            </Card>

            {/* Available Counselors */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-outfit font-bold text-gray-900 flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-purple-500" />
                  Available Counselors
                </h2>
                <Badge className="bg-purple-100 text-purple-700">{counselors.length}</Badge>
              </div>

              <div className="space-y-3 max-h-[400px] overflow-y-auto" data-testid="counselors-list">
                {counselors.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No counselors available</p>
                ) : (
                  counselors.map((counselor, index) => (
                    <motion.div
                      key={counselor._id || index}
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-center justify-between p-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center text-white font-semibold">
                          {counselor.firstName?.[0]}{counselor.lastName?.[0]}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{counselor.firstName} {counselor.lastName}</p>
                          <p className="text-xs text-gray-500">{counselor.activeAssignments || counselor.assignedStudents?.length || 0} active assignments</p>
                        </div>
                      </div>
                      <Badge className="bg-green-100 text-green-700">Available</Badge>
                    </motion.div>
                  ))
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Recent Assignments */}
        <Card className="p-6">
          <h2 className="text-lg font-outfit font-bold text-gray-900 mb-4">Recent Assignments</h2>
          <div className="space-y-3" data-testid="recent-assignments-list">
            {assignedRequests.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No recent assignments</p>
            ) : (
              assignedRequests.slice(0, 5).map((request, index) => {
                const student = request.student;
                const firstName = student?.user?.firstName || student?.firstName || 'Unknown';
                const lastName = student?.user?.lastName || student?.lastName || '';
                const counselor = request.assignedCounselor;

                return (
                  <motion.div
                    key={request.requestId || request._id || index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center justify-between p-3 bg-stone-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                        {firstName[0]}{lastName[0]}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{firstName} {lastName}</p>
                        <p className="text-xs text-gray-500">
                          Assigned to {counselor ? `${counselor.firstName} ${counselor.lastName}` : 'counselor'}
                          {request.assignedAt && ` • ${formatDistanceToNow(new Date(request.assignedAt), { addSuffix: true })}`}
                        </p>
                      </div>
                    </div>
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Assigned
                    </Badge>
                  </motion.div>
                );
              })
            )}
          </div>
        </Card>

        {/* Assignment Modal */}
        <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-primary" />
                Assign Counselor/Agent
              </DialogTitle>
            </DialogHeader>

            {selectedRequest && (
              <div className="space-y-4">
                {/* Student Info */}
                <div className="p-4 bg-stone-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Student</p>
                  <p className="font-medium text-gray-900">
                    {selectedStudent?.user?.firstName || selectedStudent?.firstName} {selectedStudent?.user?.lastName || selectedStudent?.lastName}
                  </p>
                  <p className="text-sm text-gray-500 mt-2">Service Type</p>
                  <p className="font-medium text-gray-900 capitalize">
                    {selectedRequest.serviceType?.replace(/-/g, ' ') || 'Service Request'}
                  </p>
                </div>

                {/* Info message */}
                <p className="text-sm text-blue-600 bg-blue-50 p-2 rounded-lg">
                  Select at least one: Counselor or Agent
                </p>

                {/* Counselor Selection */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Select Counselor
                  </label>
                  <Select value={selectedCounselor || 'none'} onValueChange={(val) => setSelectedCounselor(val === 'none' ? '' : val)}>
                    <SelectTrigger data-testid="counselor-select">
                      <SelectValue placeholder="Select a counselor (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Counselor</SelectItem>
                      {counselors.map((counselor) => (
                        <SelectItem key={counselor.userId || counselor._id} value={counselor.userId || counselor._id}>
                          <div className="flex items-center gap-2">
                            <span>{counselor.firstName} {counselor.lastName}</span>
                            <span className="text-xs text-gray-500">
                              ({counselor.activeServiceRequests || counselor.assignedStudents || 0} active)
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Agent Selection (Optional) */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Select Agent (Optional)
                  </label>
                  <Select value={selectedAgent || 'none'} onValueChange={(val) => setSelectedAgent(val === 'none' ? '' : val)}>
                    <SelectTrigger data-testid="agent-select">
                      <SelectValue placeholder="Select an agent (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Agent</SelectItem>
                      {agents.map((agent) => (
                        <SelectItem key={agent.userId || agent._id} value={agent.userId || agent._id}>
                          <div className="flex items-center gap-2">
                            <span>{agent.firstName} {agent.lastName}</span>
                            <span className="text-xs text-gray-500">
                              ({agent.assignedStudents || 0} active)
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setAssignModalOpen(false);
                      setSelectedRequest(null);
                      setSelectedStudent(null);
                      setSelectedCounselor('');
                      setSelectedAgent('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAssign}
                    disabled={assigning || (!selectedCounselor && !selectedAgent)}
                  >
                    {assigning ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Assigning...
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4 mr-2" />
                        Assign
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
